import { useState } from 'react';

interface FooterProps {
  onLinkClick: () => void;
}

export default function Footer({ onLinkClick }: FooterProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setSubmitMessage('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d68pjntbiueuoqm1rkkg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you for subscribing!');
        setEmail('');
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer id="contact" className="relative bg-black rounded-t-[40px] border-t-4 border-red-600">
      <div className="max-w-7xl mx-auto px-6 md:px-20 py-20">
        {/* Upper Section */}
        <div className="grid md:grid-cols-12 gap-12 mb-16">
          {/* Newsletter - Left Column */}
          <div className="md:col-span-5">
            <h3 className="text-5xl font-light text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Stay Connected
            </h3>
            <p className="text-gray-400 text-base leading-relaxed mb-12">
              I'm always up for a new connection :)
            </p>

            <form onSubmit={handleSubscribe} data-readdy-form id="newsletter-form">
              <div className="mb-8">
                <input 
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full bg-transparent border-b border-gray-700 text-white text-lg py-3 focus:outline-none focus:border-red-600 transition-colors duration-300"
                  disabled={isSubmitting}
                />
              </div>
              
              <button 
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-3 bg-white text-black text-base font-semibold rounded-full hover:bg-gray-200 transition-all duration-300 inline-flex items-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                <i className="ri-arrow-right-line text-lg"></i>
              </button>

              {submitMessage && (
                <p className={`mt-4 text-sm ${submitMessage.includes('Thank you') ? 'text-green-400' : 'text-red-400'}`}>
                  {submitMessage}
                </p>
              )}
            </form>
          </div>

          {/* Quick Links - Middle Column */}
          <div className="md:col-span-3">
            <h4 className="text-red-600 text-xs font-bold uppercase tracking-wider mb-6">Explore</h4>
            <nav className="space-y-5">
              <button onClick={onLinkClick} className="block text-white text-base hover:text-red-500 transition-colors duration-300 hover:underline cursor-pointer text-left">
                About Thomas
              </button>
              <button onClick={onLinkClick} className="block text-white text-base hover:text-red-500 transition-colors duration-300 hover:underline cursor-pointer text-left">
                Performance Types
              </button>
              <button onClick={onLinkClick} className="block text-white text-base hover:text-red-500 transition-colors duration-300 hover:underline cursor-pointer text-left">
                Contact
              </button>
            </nav>
          </div>

          {/* Contact Info - Right Column */}
          <div className="md:col-span-4">
            <h4 className="text-red-600 text-xs font-bold uppercase tracking-wider mb-6">Get in Touch</h4>
            <div className="space-y-4 mb-12">
              <p className="text-white text-lg">+33 6 79 78 10 38</p>
              <p className="text-white text-lg">guestmagician@gmail.com</p>
            </div>

            <h4 className="text-red-600 text-xs font-bold uppercase tracking-wider mb-4">Based In</h4>
            <p className="text-gray-400 text-base">on a permantent tour</p>
            <p className="text-gray-400 text-base">Worldwide</p>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-800 mb-12"></div>

        {/* Lower Section */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          {/* Social Links */}
          <div className="flex items-center gap-6">
            <button onClick={onLinkClick} className="text-white hover:text-red-500 transition-colors duration-300 hover:underline text-sm cursor-pointer">
              Instagram
            </button>
            <span className="text-gray-700">|</span>
            <button onClick={onLinkClick} className="text-white hover:text-red-500 transition-colors duration-300 hover:underline text-sm cursor-pointer">
              Facebook
            </button>
            <span className="text-gray-700">|</span>
            <button onClick={onLinkClick} className="text-white hover:text-red-500 transition-colors duration-300 hover:underline text-sm cursor-pointer">
              YouTube
            </button>
            <span className="text-gray-700">|</span>
            <button onClick={onLinkClick} className="text-white hover:text-red-500 transition-colors duration-300 hover:underline text-sm cursor-pointer">
              LinkedIn
            </button>
          </div>

          {/* Copyright */}
          <div className="text-gray-500 text-sm text-center">
            © 2025 Thomas. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-red-500 transition-colors duration-300 cursor-pointer">Website Builder</a>
          </div>

          {/* Policy Links */}
          <div className="flex items-center gap-6">
            <button onClick={onLinkClick} className="text-white hover:text-red-500 transition-colors duration-300 hover:underline text-sm cursor-pointer">
              Privacy Policy
            </button>
            <span className="text-gray-700">|</span>
            <button onClick={onLinkClick} className="text-white hover:text-red-500 transition-colors duration-300 hover:underline text-sm cursor-pointer">
              Terms of Service
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
